import React, { memo, useMemo } from 'react';
import { FixedSizeList as List, type ListChildComponentProps } from 'react-window';
import { useElementSize } from '../../hooks/useElementSize';
import { UploadRow } from '../../utils/uploadTypes';
import FileItem from './FileItem';

interface FileListProps {
  rows: UploadRow[];
  /** Index into the UNFILTERED row array, so the toggle cannot shift the marker. */
  activeIndex: number;
  indices: number[];
}

interface RowData {
  rows: UploadRow[];
  indices: number[];
  activeIndex: number;
}

const ITEM_HEIGHT = 80;
const OVERSCAN_COUNT = 5;

/**
 * Fix round 1, #1. Module scope, not a useCallback inside FileList: react-window
 * passes `children` to createElement as the element TYPE
 * (node_modules/react-window/dist/index.cjs.js:1376). A useCallback-wrapped row
 * component is still a NEW function identity whenever `rows` changes reference,
 * which useDicomUpload's `replaceRow` does on every fileProgress dispatch that
 * moves a percentage -- so React would see a different type at the same key and
 * remount every visible row instead of updating it, defeating the memoization D9
 * exists to make useful.
 * State reaches each row through react-window's itemData instead, which changes by
 * VALUE on the same stable type.
 */
function Row({ index, style, data }: ListChildComponentProps<RowData>) {
  const { rows, indices, activeIndex } = data as RowData;
  const rowIndex = indices[index];

  return (
    <div style={style}>
      <FileItem
        row={rows[rowIndex]}
        isCurrentFile={rowIndex === activeIndex}
      />
    </div>
  );
}

/**
 * D9. Both upstream and develop render a row per file — upstream unvirtualized
 * (DicomUploadProgress.tsx:372-393, a plain .filter().map() inside an
 * overflow-y-scroll div), develop virtualized. A 2,000-instance CT is ONE study
 * under D3, so upstream's shape is a cliff and develop's is the fix.
 */
const FileList = memo(({ rows, activeIndex, indices }: FileListProps) => {
  const [ref, { height, width }] = useElementSize<HTMLDivElement>();

  const itemData = useMemo<RowData>(
    () => ({ rows, indices, activeIndex }),
    [rows, indices, activeIndex]
  );

  return (
    <div
      ref={ref}
      // Fix round 1, #2. `flex-1` alone leaves the browser's default
      // `min-height: auto`, which floors this box at react-window's own inline
      // height and lets it grow but never shrink -- concretely, the failed-only
      // toggle row UploadProgress.tsx inserts at halt cannot push this box
      // smaller, so no ResizeObserver callback fires and the list never
      // re-measures down. `min-h-0` removes that floor.
      className="min-h-0 flex-1"
      data-cy="sycai-upload-list"
    >
      {/* Plan D-1: ResizeObserver reports 0 before layout, and a list of height 0
          renders nothing and never re-measures. */}
      {height > 0 && (
        <List
          height={height}
          width={width}
          itemCount={indices.length}
          itemSize={ITEM_HEIGHT}
          itemData={itemData}
          overscanCount={OVERSCAN_COUNT}
        >
          {Row}
        </List>
      )}
    </div>
  );
});

FileList.displayName = 'FileList';

export default FileList;
